// Safe JSON-response parsing, extracted from queryClient.ts so it can be
// unit-tested in isolation (queryClient.ts patches `window.fetch` at module
// load, which a Node test runner can't import).
//
// A non-JSON body — most often the SPA's index.html returned with a 200 when an
// API request loses its route during a server restart — would otherwise blow up
// with a cryptic "JSON.parse: unexpected character at line 1 column 1" error.
// This helper checks the content-type FIRST and fails loud with a friendly
// message instead. `client/src/lib/__tests__/parseJsonResponse.test.ts` locks
// the contract in.

export const FRIENDLY_NETWORK_ERROR = "Couldn't reach the server — please try again.";

export async function parseJsonResponse<T = any>(res: Response): Promise<T> {
  const contentType = res.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    throw new Error(FRIENDLY_NETWORK_ERROR);
  }
  try {
    return (await res.json()) as T;
  } catch {
    throw new Error(FRIENDLY_NETWORK_ERROR);
  }
}

// Classifies an error thrown while fetching/parsing an API response as a
// TRANSIENT blip — one a status-poll loop should ride through (keep waiting)
// rather than treat as a genuine failure. Only two cases qualify:
//   1. A raw network failure: `fetch` rejects with a TypeError BEFORE any HTTP
//      response is received (server momentarily unreachable).
//   2. A non-JSON body: the SPA's index.html served with a 200 during a server
//      restart, surfaced by parseJsonResponse as FRIENDLY_NETWORK_ERROR.
// A real HTTP failure (4xx/5xx) is thrown by apiRequest's throwIfResNotOk with
// a "<status>: <body>" message and is deliberately NOT transient, so genuine
// failures still surface promptly instead of being masked as a timeout.
export function isTransientFetchError(err: unknown): boolean {
  if (err instanceof TypeError) return true;
  return (err as { message?: unknown } | null)?.message === FRIENDLY_NETWORK_ERROR;
}
